import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, useTheme } from "@mui/material";
import { Star, StarOff, EyeOff, Eye, Settings, Heart, CheckCircle, ArrowDown, ArrowUp, Search, GripVertical, AlertTriangle, CheckSquare, List, Trash2, ShoppingCart, TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import api from "../utils/api";
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Drawer,
  Divider,
  Chip,
  Alert,
  Snackbar,
  Checkbox,
  Toolbar,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ProductTools from "../components/ProductTools";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import { useNotificationContext } from '../context/NotificationContext';

// Ajouter la liste des statuts de masquage
const MASK_STATUSES = [
  "Masqué",
  "Private Label",
  "Amazon ne partage pas",
  "Pas de vente"
];

const AnalysePage = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const sourceFile = location.state?.sourceFile;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoritesRefreshKey, setFavoritesRefreshKey] = useState(0);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showOnlyHidden, setShowOnlyHidden] = useState(false); // Nouveau state pour afficher uniquement les masqués
  const [isAddFavoriteDialogOpen, setIsAddFavoriteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [favoriteComment, setFavoriteComment] = useState("");
  const [favoriteStatus, setFavoriteStatus] = useState("Intéressant");
  // Dialogue pour masquer un produit individuel
  const [isAddMaskDialogOpen, setIsAddMaskDialogOpen] = useState(false);
  const [maskComment, setMaskComment] = useState("");
  const [maskStatus, setMaskStatus] = useState("Masqué");
  // Mise à jour pour le masquage par lot
  const [batchMaskComment, setBatchMaskComment] = useState("");
  const [batchMaskStatus, setBatchMaskStatus] = useState("Masqué");
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('analyseColumnOrder');
    return savedOrder ? JSON.parse(savedOrder) : [];
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizing = localStorage.getItem('analyseColumnSizing');
    if (savedSizing) {
      return JSON.parse(savedSizing);
    }
    // Valeurs par défaut
    return {
      productSearch: 150,
      sourceSIN: 150,
      title: 300,
      salesRank: 100,
      purchasePrice: 120,
      margin: 100,
      profit: 120,
      roi: 100,
      totalOffers: 150,
      countryCode: 100,
      vatPercentage: 100,
      estimatedNumberofSales: 150,
      column1: 80,
      tools: 100,
      buyBoxLanded: 120,
      productGroup: 150,
    };
  });
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [isBatchFavoriteDialogOpen, setIsBatchFavoriteDialogOpen] = useState(false);
  const [isConfirmBatchMaskDialogOpen, setIsConfirmBatchMaskDialogOpen] = useState(false);
  const [thresholds, setThresholds] = useState(() => {
    const saved = localStorage.getItem("analyseThresholds");
    return saved
      ? JSON.parse(saved)
      : {
          roi: { high: 50, medium: 30, enabled: true },
          profit: { high: 100, medium: 50, enabled: true },
          margin: { high: 30, medium: 15, enabled: true },
          estimatedNumberofSales: { high: 100, medium: 50, enabled: true },
        };
  });
  const [isThresholdsOpen, setIsThresholdsOpen] = useState(false);
  const [filters, setFilters] = useState({
    margin: { operator: '>=', value: '' },
    profit: { operator: '>=', value: '' },
    roi: { operator: '>=', value: '' },
    estimatedNumberofSales: { operator: '>=', value: '' },
    productGroup: { value: '' },
    countryCode: { value: '' },
    purchasePrice: { operator: '>=', value: '' },
    salesRank: { operator: '<=', value: '' }
  });
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [hiddenProducts, setHiddenProducts] = useState([]);
  const [priceChanges, setPriceChanges] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const { refreshNotifications } = useNotificationContext();

  // Mapping des codes pays vers leurs drapeaux
  const countryFlags = {
    FR: "https://flagcdn.com/24x18/fr.png",
    ES: "https://flagcdn.com/24x18/es.png",
    IT: "https://flagcdn.com/24x18/it.png",
    DE: "https://flagcdn.com/24x18/de.png"
  };

  // Définir columnHelper au niveau du composant
  const columnHelper = createColumnHelper();

  // Fonction pour charger toutes les données nécessaires
  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Charger les données en parallèle avec un timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      const dataPromise = Promise.all([
        api.get(`/api/analyse/data/${sourceFile.endsWith('.csv') ? `${sourceFile}.json` : sourceFile}`),
        api.get("/api/favoris"),
        api.get("/api/masques")
      ]);

      const [dataResponse, favoritesResponse, hiddenResponse] = await Promise.race([
        dataPromise,
        timeoutPromise
      ]);

      // Traiter les données
      if (dataResponse.data && dataResponse.data.data) {
        const filteredData = dataResponse.data.data.filter(item => 
          item.roi !== null && 
          item.margin !== null && 
          item.profit !== null && 
          item.salesRank !== null
        );
        setData(filteredData);
      } else {
        setError('Format de données invalide');
      }

      setFavorites(favoritesResponse.data);
      setHiddenProducts(hiddenResponse.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [sourceFile]);

  // Utiliser useEffect avec un debounce pour éviter les appels multiples
  useEffect(() => {
    if (sourceFile) {
      const timer = setTimeout(() => {
        fetchAllData();
      }, 300); // Debounce de 300ms
      
      return () => clearTimeout(timer);
    }
  }, [sourceFile, fetchAllData]);

  // Sauvegarder la configuration des colonnes
  useEffect(() => {
    if (columnOrder.length > 0) {
      localStorage.setItem('analyseColumnOrder', JSON.stringify(columnOrder));
    }
  }, [columnOrder]);

  useEffect(() => {
    if (Object.keys(columnSizing).length > 0) {
      localStorage.setItem('analyseColumnSizing', JSON.stringify(columnSizing));
    }
  }, [columnSizing]);

  // Sauvegarder les seuils
  useEffect(() => {
    localStorage.setItem('analyseThresholds', JSON.stringify(thresholds));
  }, [thresholds]);

  // Fonction pour vérifier si un produit est dans les favoris
  const isProductFavorite = (ean, asin, countryCode) => {
    // Vérifier d'abord s'il existe un favori spécifique pour ce produit ET ce pays
    const countrySpecificFavorite = favorites.find(fav => 
      (fav.ean === ean || fav.asin === asin) &&
      fav.countryCode === countryCode
    );
    
    if (countrySpecificFavorite) {
      return true;
    }
    
    // Ensuite, vérifier s'il existe un favori global (sans pays spécifié)
    const globalFavorite = favorites.find(fav => 
      (fav.ean === ean || fav.asin === asin) &&
      !fav.countryCode
    );
    
    return !!globalFavorite;
  };

  // Fonction pour obtenir les détails d'un favori
  const getFavoriteDetails = (ean, asin, countryCode) => {
    // Vérifier d'abord s'il existe un favori spécifique pour ce produit ET ce pays
    const countrySpecificFavorite = favorites.find(fav => 
      (fav.ean === ean || fav.asin === asin) &&
      fav.countryCode === countryCode
    );
    
    if (countrySpecificFavorite) {
      return countrySpecificFavorite;
    }
    
    // Ensuite, vérifier s'il existe un favori global (sans pays spécifié)
    return favorites.find(fav => 
      (fav.ean === ean || fav.asin === asin) &&
      !fav.countryCode
    );
  };

  // Fonction pour ajouter un produit aux favoris
  const handleAddToFavorites = (product) => {
    setSelectedProduct(product);
    setIsAddFavoriteDialogOpen(true);
  };

  // Fonction pour sauvegarder un favori
  const handleSaveFavorite = async () => {
    try {
      const newFavorite = {
        ean: selectedProduct.productSearch,
        asin: selectedProduct.sourceSIN,
        title: selectedProduct.title,
        purchasePrice: selectedProduct.purchasePrice,
        buyBoxLanded: selectedProduct.buyBoxLanded,
        supplier: sourceFile,
        comment: favoriteComment,
        status: favoriteStatus,
        column1: selectedProduct.column1,
        countryCode: selectedProduct.countryCode
      };
      
      const response = await api.post("/api/favoris", newFavorite);
      
      // Mettre à jour l'état local des favoris immédiatement avec la réponse complète du serveur
      setFavorites(prevFavorites => [...prevFavorites, response.data]);
      
      // Forcer le rafraîchissement de la colonne des favoris
      setFavoritesRefreshKey(prev => prev + 1);
      
      setIsAddFavoriteDialogOpen(false);
      setFavoriteComment('');
      setFavoriteStatus('Intéressant');
    } catch (error) {
      console.error('Erreur lors de l\'ajout aux favoris:', error);
    }
  };

  // Fonction pour supprimer un favori
  const handleRemoveFavorite = async (favoriteId) => {
    try {
      await api.delete(`/api/favoris/${favoriteId}`);
      
      // Mettre à jour l'état local des favoris immédiatement
      setFavorites(prevFavorites => prevFavorites.filter(fav => fav.id !== favoriteId));
      
      // Forcer le rafraîchissement de la colonne des favoris
      setFavoritesRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Erreur lors de la suppression du favori:", error);
    }
  };

  // Fonction pour mettre à jour un favori
  const handleUpdateFavorite = async (favoriteId, comment, status) => {
    try {
      await api.put(`/api/favoris/${favoriteId}`, { comment, status });
      await fetchAllData();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du favori:", error);
    }
  };

  // Fonction pour vérifier si un produit est masqué
  const isProductHidden = (product) => {
    // Ajouter du logging pour le diagnostic du produit spécifique
    const isDebugProduct = product.productSearch === "3545411344208";
    
    if (isDebugProduct) {
      console.log(`[DEBUG] isProductHidden pour ${product.productSearch} en ${product.countryCode}`);
    }
    
    // Vérifier d'abord s'il existe un masque spécifique pour ce produit ET ce pays
    const countrySpecificMask = hiddenProducts.find(p => 
      p.ean === product.productSearch && 
      p.asin === product.sourceSIN &&
      p.countryCode === product.countryCode
    );
    
    if (isDebugProduct) {
      console.log(`[DEBUG] Masque spécifique au pays: ${!!countrySpecificMask}`);
    }
    
    if (countrySpecificMask) {
      if (isDebugProduct) {
        console.log(`[DEBUG] Produit masqué car trouvé masque pour ce pays`);
      }
      return true;
    }
    
    // MODIFICATION: Vérifier uniquement les statuts spéciaux DANS CE PAYS SPÉCIFIQUE
    // N'appliquer les statuts spéciaux que pays par pays
    const globalStatusMask = hiddenProducts.find(p => 
      p.ean === product.productSearch && 
      p.asin === product.sourceSIN &&
      p.status !== "Masqué" &&
      p.countryCode === product.countryCode // Ajouter cette condition pour s'assurer que le statut spécial s'applique à ce pays
    );
    
    if (isDebugProduct) {
      console.log(`[DEBUG] Masque global avec statut spécial: ${!!globalStatusMask}`);
    }
    
    if (globalStatusMask) {
      if (isDebugProduct) {
        console.log(`[DEBUG] Produit masqué car statut spécial`);
      }
      return true;
    }
    
    // IMPORTANT: Ne pas considérer les masques globaux (sans pays spécifié) avec le statut "Masqué"
    // pour permettre à un produit d'être visible dans certains pays même s'il est masqué dans d'autres
    
    if (isDebugProduct) {
      console.log(`[DEBUG] Produit NON masqué`);
      
      // Vérifiez tous les masques existants pour ce produit
      const allMasks = hiddenProducts.filter(p => 
        p.ean === product.productSearch && 
        p.asin === product.sourceSIN
      );
      
      console.log(`[DEBUG] Tous les masques pour ce produit:`, allMasks);
    }
    
    return false;
  };

  // Fonction pour basculer le statut favori d'un produit
  const toggleProductFavorite = (product) => {
    const isFavorite = isProductFavorite(product.productSearch, product.sourceSIN, product.countryCode);
    const favoriteDetails = isFavorite ? getFavoriteDetails(product.productSearch, product.sourceSIN, product.countryCode) : null;
    
    if (isFavorite && favoriteDetails) {
      handleRemoveFavorite(favoriteDetails.id);
    } else {
      handleAddToFavorites(product);
    }
  };

  // Fonction pour masquer/démasquer un produit
  const toggleProductVisibility = async (product) => {
    try {
      if (isProductHidden(product)) {
        // Démasquer le produit - IMPORTANT: chercher uniquement le masque du pays spécifique
        const hiddenProduct = hiddenProducts.find(p => 
          p.ean === product.productSearch && 
          p.asin === product.sourceSIN && 
          p.countryCode === product.countryCode
        );
        
        if (hiddenProduct && hiddenProduct.id) {
          await api.delete(`/api/masques/${hiddenProduct.id}`);
          setHiddenProducts(prev => prev.filter(p => p.id !== hiddenProduct.id));
        }
      } else {
        // Ouvrir la boîte de dialogue pour masquer le produit
        setSelectedProduct(product);
        setIsAddMaskDialogOpen(true);
      }
    } catch (error) {
      console.error('Erreur lors de la modification de la visibilité:', error);
    }
  };

  // Fonction pour masquer un produit après confirmation
  const handleSaveMask = async () => {
    try {
      // Masquer le produit avec tous les champs nécessaires
      const response = await api.post("/api/masques", {
        ean: selectedProduct.productSearch,
        asin: selectedProduct.sourceSIN,
        title: selectedProduct.title,
        purchasePrice: selectedProduct.purchasePrice,
        buyBoxLanded: selectedProduct.buyBoxLanded,
        supplier: sourceFile,
        column1: selectedProduct.column1,
        status: maskStatus,
        comment: maskComment,
        countryCode: selectedProduct.countryCode // Ajouter le pays
      });
      
      setHiddenProducts(prev => [...prev, response.data]);
      
      // Filtrer les données pour retirer le produit masqué de la vue
      setData(prev => prev.filter(item => 
        !(item.productSearch === selectedProduct.productSearch && 
          item.sourceSIN === selectedProduct.sourceSIN &&
          item.countryCode === selectedProduct.countryCode)
      ));
      
      // Réinitialiser
      setIsAddMaskDialogOpen(false);
      setMaskComment('');
      setMaskStatus('Masqué');
      setSelectedProduct(null);
    } catch (error) {
      console.error('Erreur lors du masquage:', error);
    }
  };

  // Fonction pour créer les colonnes avec le mapping spécifié
  const createColumns = (data) => {
    if (!data || data.length === 0) return [];

    // Ajouter la colonne checkbox en premier
    const checkboxColumn = columnHelper.display({
      id: 'selection',
      header: () => (
        <Checkbox
          checked={selectAll}
          indeterminate={selectedProducts.length > 0 && selectedProducts.length < table.getRowModel().rows.length}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'Sélectionner tout' }}
          size="small"
        />
      ),
      cell: ({ row }) => {
        // Utiliser une clé composite pour identifier de manière unique un produit (EAN + pays)
        const productId = `${row.original.productSearch}-${row.original.countryCode || 'global'}`;
        // Vérifier si ce produit est actuellement sélectionné
        const isSelected = selectedProducts.includes(productId);
        
        return (
          <Checkbox
            checked={isSelected}
            onChange={() => {
              // Mettre à jour la liste des produits sélectionnés
              setSelectedProducts(prev => {
                if (prev.includes(productId)) {
                  return prev.filter(id => id !== productId);
                } else {
                  return [...prev, productId];
                }
              });
            }}
            onClick={(e) => e.stopPropagation()}
            inputProps={{ 'aria-label': 'Sélectionner une ligne' }}
            size="small"
          />
        );
      },
      enableSorting: false,
      size: 40,  // Réduire la taille de la colonne
    });

    const columnMapping = {
      productSearch: { header: "EAN", size: 150 },
      sourceSIN: { header: "ASIN", size: 150 },
      title: { header: "Titre", size: 300 },
      salesRank: { header: "Classement", size: 100 },
      purchasePrice: { header: "Prix d'achat", size: 120 },
      margin: { header: "Marge", size: 100 },
      profit: { header: "Profit", size: 120 },
      roi: { header: "ROI", size: 100 },
      totalOffers: { header: "Offres", size: 150 },
      countryCode: { header: "Pays", size: 100 },
      vatPercentage: { header: "TVA %", size: 100 },
      estimatedNumberofSales: { header: "Ventes", size: 150 },
      column1: { header: "Fournisseur", size: 80 },
      buyBoxLanded: { header: "Buy Box", size: 120 },
      productGroup: { header: "Catégorie", size: 150 },
      tools: { header: "Outils", size: 100 },
      actions: { header: "Actions", size: 100 }
    };

    const columns = Object.entries(columnMapping).map(([key, config]) => {
      const formatValue = (value) => {
        if (value === null || value === undefined) return "-";
        
        if (typeof value === "number") {
          let formattedValue;
          if (key === "purchasePrice" || key === "profit") {
            formattedValue = `${value.toFixed(2)} €`;
          } else if (key === "roi" || key === "margin" || key === "vatPercentage") {
            formattedValue = `${value.toFixed(2)}%`;
          } else if (key === "salesRank") {
            formattedValue = value.toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 });
          } else {
            formattedValue = value.toString();
          }

          if (key === "roi" || key === "profit" || key === "margin" || key === "estimatedNumberofSales") {
            const thresholdKey = key;
            
            if (thresholds[thresholdKey]) {
              const colors = getColorForValue(value, thresholds[thresholdKey]);
              return (
                <span style={{ 
                  backgroundColor: colors.bg,
                  color: colors.text,
                  fontWeight: '600',
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  textAlign: 'right',
                  margin: '-6px -8px',
                  padding: '6px 8px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {formattedValue}
                </span>
              );
            } 
          } 

          return formattedValue;
        }
        
        return value.toString();
      };

      if (key === "countryCode") {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => {
            const countryCode = info.getValue();
            const flagUrl = countryFlags[countryCode];
            return flagUrl ? (
              <img 
                src={flagUrl} 
                alt={`Drapeau ${countryCode}`}
                style={{ 
                  width: '24px',
                  height: '18px',
                  objectFit: 'cover',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            ) : countryCode;
          },
          enableSorting: true,
          size: config.size,
          enableResizing: true,
          filterFn: 'equals',
          enableColumnFilter: true,
        });
      }

      if (key === "productGroup") {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => formatValue(info.getValue()),
          enableSorting: true,
          size: config.size,
          enableResizing: true,
          filterFn: 'equals',
          enableColumnFilter: true,
        });
      }

      if (key === "column1") {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => {
            const url = info.getValue();
            return url ? (
              <Tooltip title="Voir le produit sur le site fournisseur">
                <IconButton
                  size="small"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ShoppingCart size={20} />
                </IconButton>
              </Tooltip>
            ) : "-";
          },
          enableSorting: true,
          size: config.size,
          enableResizing: true,
        });
      }

      if (key === "buyBoxLanded") {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => {
            const value = info.getValue(); // Prix Buy Box actuel
            const product = info.row.original;
            
            // Vérifier si le produit est masqué
            const isHidden = isProductHidden(product);
            
            // Vérifier si le produit est un favori
            const isFavorite = isProductFavorite(product.productSearch, product.sourceSIN, product.countryCode);
            
            // Si le produit est masqué, récupérer ses informations
            const hiddenProduct = isHidden ? 
              hiddenProducts.find(p => 
                p.ean === product.productSearch && 
                p.asin === product.sourceSIN && 
                (!p.countryCode || p.countryCode === product.countryCode)
              ) : 
              null;
              
            // Si le produit est un favori, récupérer ses informations
            const favoriteProduct = isFavorite ?
              getFavoriteDetails(product.productSearch, product.sourceSIN, product.countryCode) :
              null;
            
            // Variables pour les changements de prix
            let hasPriceIncreased = false;
            let oldPrice = null;
            let priceChangeSource = null;
            
            // Vérifier d'abord dans les variations de prix stockées (priorité absolue)
            // Utiliser une clé qui inclut le pays
            const productKey = `${product.productSearch}-${product.countryCode || 'global'}`;
            if (priceChanges[productKey]?.buyBox) {
              oldPrice = priceChanges[productKey].buyBox.oldPrice;
              hasPriceIncreased = priceChanges[productKey].buyBox.increased;
              priceChangeSource = "cache";
            }
            // Vérifier pour les produits masqués (priorité secondaire)
            else if (hiddenProduct && hiddenProduct.buyBoxLanded !== undefined) {
              oldPrice = Number(hiddenProduct.buyBoxLanded);
              const currentPrice = Number(value);
              hasPriceIncreased = currentPrice > oldPrice;
              priceChangeSource = "masqué";
            } 
            // Ensuite vérifier pour les favoris si pas déjà détecté un changement de prix
            else if (favoriteProduct && favoriteProduct.buyBoxLanded !== undefined) {
              oldPrice = Number(favoriteProduct.buyBoxLanded);
              const currentPrice = Number(value);
              hasPriceIncreased = currentPrice > oldPrice;
              priceChangeSource = "favori";
            }
            
            // Si on a détecté un changement de prix, afficher l'indicateur
            if (oldPrice !== null && hasPriceIncreased) {
              const currentPrice = Number(value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>
                    {`${currentPrice.toFixed(2)} €`}
                  </span>
                  <Tooltip title={`Prix augmenté de ${oldPrice.toFixed(2)} € à ${currentPrice.toFixed(2)} € (+${(currentPrice - oldPrice).toFixed(2)} €) [${priceChangeSource}]`}>
                    <TrendingUp size={16} style={{ color: 'red' }} />
                  </Tooltip>
                </Box>
              );
            }
            
            // Affichage normal sans changement de prix
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>
                  {`${Number(value).toFixed(2)} €`}
                </span>
              </Box>
            );
          },
          enableSorting: true,
          size: config.size,
          enableResizing: true,
        });
      }

      if (key === "purchasePrice") {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => {
            const value = info.getValue(); // Prix d'achat actuel dans le fichier analysé
            const product = info.row.original;
            
            // Vérifier si le produit est masqué
            const isHidden = isProductHidden(product);
            
            // Vérifier si le produit est un favori
            const isFavorite = isProductFavorite(product.productSearch, product.sourceSIN, product.countryCode);
            
            // Si le produit est masqué, récupérer ses informations
            const hiddenProduct = isHidden ? 
              hiddenProducts.find(p => 
                p.ean === product.productSearch && 
                p.asin === product.sourceSIN && 
                (!p.countryCode || p.countryCode === product.countryCode)
              ) : 
              null;
              
            // Si le produit est un favori, récupérer ses informations
            const favoriteProduct = isFavorite ?
              getFavoriteDetails(product.productSearch, product.sourceSIN, product.countryCode) :
              null;
            
            // Variables pour les changements de prix
            let hasPriceDecreased = false;
            let oldPrice = null;
            let priceChangeSource = null;
            
            // Vérifier d'abord dans les variations de prix stockées (priorité absolue)
            // Utiliser une clé qui inclut le pays
            const productKey = `${product.productSearch}-${product.countryCode || 'global'}`;
            if (priceChanges[productKey]?.purchasePrice) {
              oldPrice = priceChanges[productKey].purchasePrice.oldPrice;
              hasPriceDecreased = priceChanges[productKey].purchasePrice.decreased;
              priceChangeSource = "cache";
            }
            // Vérifier pour les produits masqués (priorité secondaire)
            else if (hiddenProduct && hiddenProduct.purchasePrice !== undefined) {
              oldPrice = Number(hiddenProduct.purchasePrice);
              const currentPrice = Number(value);
              hasPriceDecreased = currentPrice < oldPrice;
              priceChangeSource = "masqué";
            } 
            // Ensuite vérifier pour les favoris si pas déjà détecté un changement de prix
            else if (favoriteProduct && favoriteProduct.purchasePrice !== undefined) {
              oldPrice = Number(favoriteProduct.purchasePrice);
              const currentPrice = Number(value);
              hasPriceDecreased = currentPrice < oldPrice;
              priceChangeSource = "favori";
            }
            
            // Si on a détecté un changement de prix, afficher l'indicateur
            if (oldPrice !== null && hasPriceDecreased) {
              const currentPrice = Number(value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>
                    {`${currentPrice.toFixed(2)} €`}
                  </span>
                  <Tooltip title={`Prix diminué de ${oldPrice.toFixed(2)} € à ${currentPrice.toFixed(2)} € (-${(oldPrice - currentPrice).toFixed(2)} €) [${priceChangeSource}]`}>
                    <TrendingDown size={16} style={{ color: 'green' }} />
                  </Tooltip>
                </Box>
              );
            }
            
            // Affichage normal sans changement de prix
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>
                  {`${Number(value).toFixed(2)} €`}
                </span>
              </Box>
            );
          },
          enableSorting: true,
          size: config.size,
          enableResizing: true,
          filterFn: 'custom',
          enableColumnFilter: true
        });
      }

      if (key === "salesRank") {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => formatValue(info.getValue()),
          enableSorting: true,
          size: config.size,
          enableResizing: true,
          filterFn: 'custom',
          enableColumnFilter: true
        });
      }

      // Ajouter la configuration de filtrage pour les colonnes avec filtres
      if (filters[key]) {
        return columnHelper.accessor(key, {
          id: key,
          header: config.header,
          cell: (info) => formatValue(info.getValue()),
          enableSorting: true,
          size: config.size,
          enableResizing: true,
          filterFn: 'custom',
          enableColumnFilter: true,
        });
      }

      if (key === "tools") {
        return columnHelper.display({
          id: key,
          header: config.header || "Outils",
          cell: (info) => {
            const product = info.row.original;
            return <ProductTools asin={product.sourceSIN} countryCode={product.countryCode} />;
          },
          enableSorting: false,
          size: config.size || 100,
          enableResizing: true,
        });
      }

      if (key === "actions") {
        return columnHelper.display({
          id: key,
          header: config.header || "Actions",
          cell: (info) => {
            const product = info.row.original;
            
            // Ajouter du logging pour le diagnostic du produit spécifique
            if (product.productSearch === "3545411344208") {
              console.log(`[DEBUG ACTIONS] Produit ${product.productSearch} en ${product.countryCode}`);
            }
            
            // Vérifier si ce produit spécifique (dans ce pays spécifique) est masqué
            // Nous ne devons PAS utiliser isProductHidden qui peut tenir compte d'autres règles
            // Nous devons vérifier spécifiquement si un masque existe pour ce produit dans ce pays
            const isHiddenInThisCountry = hiddenProducts.some(p => 
              p.ean === product.productSearch && 
              p.asin === product.sourceSIN &&
              p.countryCode === product.countryCode
            );
            
            if (product.productSearch === "3545411344208") {
              console.log(`[DEBUG ACTIONS] Masqué dans ce pays: ${isHiddenInThisCountry}`);
            }
            
            const isFavorite = isProductFavorite(product.productSearch, product.sourceSIN, product.countryCode);
            
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Bouton Favoris */}
                <Tooltip title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProductFavorite(product);
                    }}
                  >
                    {isFavorite ? (
                      <Star size={16} style={{ color: "#FFB900" }} />
                    ) : (
                      <StarOff size={16} />
                    )}
                  </IconButton>
                </Tooltip>
                
                {/* Bouton Masquer/Afficher */}
                <Tooltip title={isHiddenInThisCountry 
                  ? `Afficher (${hiddenProducts.find(p => 
                      p.ean === product.productSearch && 
                      p.asin === product.sourceSIN && 
                      p.countryCode === product.countryCode
                    )?.status || 'Masqué'})` 
                  : "Masquer"
                }>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProductVisibility(product);
                    }}
                  >
                    {isHiddenInThisCountry ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
          enableSorting: false,
          size: config.size || 100,
          enableResizing: true,
        });
      }

      // Supprimer la colonne favorite si elle existe
      if (key === "favorite") {
        return null;
      }

      // Supprimer la colonne visibility si elle existe
      if (key === "visibility") {
        return null;
      }

      return columnHelper.accessor(key, {
        id: key,
        header: config.header,
        cell: (info) => formatValue(info.getValue()),
        enableSorting: true,
        size: config.size,
        enableResizing: true,
      });
    });

    // Insérer la colonne de sélection en premier
    return [checkboxColumn, ...columns];
  };

  // Création des colonnes à partir des données
  const columns = useMemo(() => createColumns(data), [data, thresholds, favoritesRefreshKey, selectedProducts]);

  // Initialiser l'ordre des colonnes
  useEffect(() => {
    if (columns.length > 0 && columnOrder.length === 0) {
      const initialOrder = columns.map(col => col.id);
      setColumnOrder(initialOrder);
    }
  }, [columns, columnOrder]);

  // Filtrer les données pour n'afficher que les favoris/masqués si l'option est activée
  const filteredData = useMemo(() => {
    // Commencer avec toutes les données
    let filtered = data;
    
    // Préparer un objet pour collecter les variations de prix
    const newPriceChanges = {};
    
    // IMPORTANT: Inclure d'abord tous les produits qui sont en favoris, quels que soient les filtres
    const allFavorites = data.filter(item => 
      isProductFavorite(item.productSearch, item.sourceSIN, item.countryCode)
    );
    
    // Filtrer pour n'afficher que les favoris si cette option est activée
    if (showOnlyFavorites) {
      filtered = allFavorites;
    } else if (showOnlyHidden) {
      // FILTRE 2: Afficher uniquement les produits masqués
      // IMPORTANT: Cette partie doit être complètement isolée des autres logiques
      
      console.log("[DEBUG] Mode masqués activé - filtrage strict");
      
      // Vider complètement filtered et repartir du début pour éviter tout héritage de filtres précédents
      filtered = [];
      
      // Parcourir tous les produits du dataset original
      data.forEach(item => {
        // VÉRIFICATION CLÉ: Le produit doit être masqué DANS CE PAYS SPÉCIFIQUE
        // Note: On compare explicitement avec le pays exact pour éviter les erreurs
        const exactCountryMask = hiddenProducts.find(p => 
          p.ean === item.productSearch && 
          p.asin === item.sourceSIN &&
          p.countryCode === item.countryCode
        );
        
        // Vérification spéciale: produit avec un statut autre que "Masqué"
        const specialStatusMask = hiddenProducts.find(p => 
          p.ean === item.productSearch && 
          p.asin === item.sourceSIN &&
          p.status !== "Masqué" &&
          p.countryCode === item.countryCode // Ajouter cette condition pour s'assurer que le statut spécial s'applique à ce pays
        );
        
        // Journaliser pour déboguer
        if (item.productSearch === "3545411344208") {
          console.log(`[DEBUG] ${item.productSearch} en ${item.countryCode} - Masque exact pays: ${!!exactCountryMask}, Statut spécial: ${!!specialStatusMask}`);
          
          if (exactCountryMask) {
            console.log(`[DEBUG] Détails masque: Pays=${exactCountryMask.countryCode}, Statut=${exactCountryMask.status}`);
          }
        }
        
        // N'ajouter que si on a un masque EXACT pour ce pays ou un statut spécial
        if (exactCountryMask || specialStatusMask) {
          filtered.push(item);
        }
      });
      
      // Journaliser les résultats
      console.log("[DEBUG] Produits après filtrage strict:", filtered.map(i => `${i.productSearch} en ${i.countryCode}`));
      
      // Ajouter une alerte de débogage à l'écran
      if (filtered.length > 0) {
        console.log(`[DEBUG] Nombre de produits masqués trouvés: ${filtered.length}`);
        
        // Liste des EANs uniques pour vérification
        const uniqueEANs = [...new Set(filtered.map(item => item.productSearch))];
        console.log(`[DEBUG] EANs uniques masqués: ${uniqueEANs.join(', ')}`);
        
        // Vérifier les pays pour chaque EAN problématique
        if (uniqueEANs.includes("3545411344208")) {
          const countriesForProblemEAN = filtered
            .filter(item => item.productSearch === "3545411344208")
            .map(item => item.countryCode);
          console.log(`[DEBUG] Pays pour 3545411344208: ${countriesForProblemEAN.join(', ')}`);
        }
      } else {
        console.log("[DEBUG] Aucun produit masqué trouvé!");
      }
    } else {
      // Appliquer les filtres principaux
      filtered = data.filter(item => {
        // Si c'est un favori, toujours l'afficher quelle que soit sa visibilité
        if (isProductFavorite(item.productSearch, item.sourceSIN, item.countryCode)) {
          return true;
        }
        
        // Pour les non-favoris, vérifier s'ils sont masqués
        const isHidden = isProductHidden(item);
        
        // Garder tous les produits non masqués
        if (!isHidden) return true;
        
        // À partir d'ici, tous les produits sont masqués
        
        // Récupérer UNIQUEMENT le masque spécifique au pays en question
        const hiddenProduct = hiddenProducts.find(p => 
          p.ean === item.productSearch && 
          p.asin === item.sourceSIN && 
          p.countryCode === item.countryCode
        );
        
        // Si pas de masque spécifique au pays actuel, vérifier si c'est un statut autre que "Masqué"
        if (!hiddenProduct) {
          const globalStatusMask = hiddenProducts.find(p => 
            p.ean === item.productSearch && 
            p.asin === item.sourceSIN && 
            p.status !== "Masqué"
          );
          
          // Si c'est un statut spécial, on le masque systématiquement
          if (globalStatusMask) return false;
          
          // Si nous arrivons ici, c'est qu'il n'y a pas de masque pour ce pays spécifique
          // et pas de statut spécial, donc on l'affiche
          return true;
        }
        
        // Si le statut n'est pas "Masqué", on le cache systématiquement
        if (hiddenProduct.status !== "Masqué") return false;
        
        // Vérifier si la Buy Box a RÉELLEMENT augmenté
        const currentBuyBox = parseFloat(item.buyBoxLanded) || 0;
        const oldBuyBox = parseFloat(hiddenProduct.buyBoxLanded) || 0;
        const buyBoxIncreased = currentBuyBox > oldBuyBox;
        
        // Vérifier si le prix d'achat a RÉELLEMENT diminué
        const currentPurchasePrice = parseFloat(item.purchasePrice) || 0;
        const oldPurchasePrice = parseFloat(hiddenProduct.purchasePrice) || 0;
        const purchasePriceDecreased = currentPurchasePrice < oldPurchasePrice;
        
        // Afficher les détails pour débogage si les EANs correspondent
        if (item.productSearch === "4895229106215" || item.productSearch === "3545411344208") {
          // Stocker les variations de prix pour ce produit dans l'objet temporaire
          // Utiliser une clé qui inclut le pays pour éviter les conflits
          const productKey = `${item.productSearch}-${item.countryCode || 'global'}`;
          newPriceChanges[productKey] = {
            buyBox: {
              oldPrice: oldBuyBox,
              newPrice: currentBuyBox,
              increased: buyBoxIncreased
            },
            purchasePrice: {
              oldPrice: oldPurchasePrice,
              newPrice: currentPurchasePrice,
              decreased: purchasePriceDecreased
            }
          };
          
          // Ne plus forcer l'affichage de ce produit pour débogage, respecter les règles normales
          return buyBoxIncreased || purchasePriceDecreased;
        }
        
        // Ne garder le produit masqué QUE s'il présente une opportunité
        const shouldShow = buyBoxIncreased || purchasePriceDecreased;
        
        // Si le produit doit être affiché, stocker ses variations de prix
        if (shouldShow) {
          // Utiliser une clé qui inclut le pays pour éviter les conflits
          const productKey = `${item.productSearch}-${item.countryCode || 'global'}`;
          newPriceChanges[productKey] = {
            buyBox: {
              oldPrice: oldBuyBox,
              newPrice: currentBuyBox,
              increased: buyBoxIncreased
            },
            purchasePrice: {
              oldPrice: oldPurchasePrice,
              newPrice: currentPurchasePrice,
              decreased: purchasePriceDecreased
            }
          };
        }
        
        return shouldShow;
      });
    }
    
    // NOUVEAU : INCLURE les favoris qui ne sont pas dans le filtre mais qui ont des opportunités
    // SEULEMENT si on n'affiche pas déjà tous les favoris
    if (!showOnlyFavorites) {
      // Pour chaque produit favori, vérifier s'il présente une opportunité
      allFavorites.forEach(item => {
        const favoriteDetails = getFavoriteDetails(item.productSearch, item.sourceSIN, item.countryCode);
        
        if (favoriteDetails) {
          // Vérifier si la Buy Box a augmenté par rapport au prix favori
          const buyBoxIncreased = Number(item.buyBoxLanded) > Number(favoriteDetails.buyBoxLanded);
          
          // Vérifier si le prix d'achat a diminué par rapport au prix favori
          const purchasePriceDecreased = Number(item.purchasePrice) < Number(favoriteDetails.purchasePrice);
          
          // Si le produit présente une opportunité, s'assurer qu'il est inclus dans filtered
          if (buyBoxIncreased || purchasePriceDecreased) {
            // S'assurer que le produit n'est pas déjà dans filtered
            const isAlreadyIncluded = filtered.some(
              filteredItem => 
                filteredItem.productSearch === item.productSearch && 
                filteredItem.sourceSIN === item.sourceSIN &&
                filteredItem.countryCode === item.countryCode
            );
            
            // Si le produit n'est pas déjà inclus, l'ajouter
            if (!isAlreadyIncluded) {
              filtered.push(item);
            }
          }
        }
      });
    }
    
    return { filtered, newPriceChanges };
  }, [data, showOnlyFavorites, showOnlyHidden, favorites, hiddenProducts]);

  // Extraire les résultats du useMemo
  const { filtered: finalFilteredData, newPriceChanges } = filteredData;

  // Mettre à jour priceChanges dans un useEffect séparé pour éviter les re-rendus infinis
  useEffect(() => {
    setPriceChanges(prev => ({
      ...prev,
      ...newPriceChanges
    }));
  }, [newPriceChanges]);

  const table = useReactTable({
    data: finalFilteredData,
    columns,
    state: { 
      globalFilter, 
      pagination, 
      sorting,
      columnOrder,
      columnSizing,
      columnFilters: Object.entries(filters).map(([id, filter]) => {
        return {
          id,
          value: filter
        };
      })
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnOrderChange: (newOrder) => {
      setColumnOrder(newOrder);
    },
    onColumnSizingChange: (newSizing) => {
      setColumnSizing(newSizing);
      localStorage.setItem('analyseColumnSizing', JSON.stringify(newSizing));
    },
    onColumnFiltersChange: (updater) => {
      if (typeof updater === 'function') {
        const newFilters = updater(
          Object.entries(filters).map(([id, filter]) => ({
            id,
            value: filter
          }))
        );
        
        const updatedFilters = {};
        newFilters.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      } else {
        const updatedFilters = {};
        updater.forEach(filter => {
          updatedFilters[filter.id] = filter.value;
        });
        
        setFilters(updatedFilters);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableColumnResizing: true,
    enableColumnOrdering: true,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150,
    },
    filterFns: {
      custom: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        const value = parseFloat(row.getValue(columnId));
        if (isNaN(value)) return true;

        const filterValueNum = parseFloat(filterValue.value);
        if (isNaN(filterValueNum)) return true;

        switch (filterValue.operator) {
          case '>=':
            return value >= filterValueNum;
          case '>':
            return value > filterValueNum;
          case '<=':
            return value <= filterValueNum;
          case '<':
            return value < filterValueNum;
          case '=':
            return value === filterValueNum;
          default:
            return true;
        }
      },
      equals: (row, columnId, filterValue) => {
        if (!filterValue || !filterValue.value) return true;
        
        const value = row.getValue(columnId);
        return value === filterValue.value;
      }
    },
    autoResetPageIndex: false,
  });

  // Ajouter un bouton pour réinitialiser la configuration
  const resetConfiguration = () => {
    localStorage.removeItem('analyseColumnOrder');
    localStorage.removeItem('analyseColumnSizing');
    localStorage.removeItem('analyseThresholds');
    setColumnOrder([]);
    setColumnSizing({
      productSearch: 150,
      sourceSIN: 150,
      title: 300,
      salesRank: 100,
      purchasePrice: 120,
      margin: 100,
      profit: 120,
      roi: 100,
      totalOffers: 150,
      countryCode: 100,
      vatPercentage: 100,
      estimatedNumberofSales: 150,
      column1: 80,
      tools: 100,
      buyBoxLanded: 120,
      productGroup: 150,
    });
    setThresholds({
      roi: { high: 50, medium: 30, enabled: true },
      profit: { high: 100, medium: 50, enabled: true },
      margin: { high: 30, medium: 15, enabled: true },
      estimatedNumberofSales: { high: 100, medium: 50, enabled: true },
    });
    setFilters({
      margin: { operator: '>=', value: '' },
      profit: { operator: '>=', value: '' },
      roi: { operator: '>=', value: '' },
      estimatedNumberofSales: { operator: '>=', value: '' },
      productGroup: { value: '' },
      countryCode: { value: '' },
      purchasePrice: { operator: '>=', value: '' },
      salesRank: { operator: '<=', value: '' }
    });
  };

  const pageCount = table.getPageCount() || 0;

  // Fonction pour obtenir la couleur en fonction de la valeur et des seuils
  const getColorForValue = (value, thresholds) => {
    if (value === null || value === undefined) return { bg: 'inherit', text: 'inherit' };
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return { bg: 'inherit', text: 'inherit' };
    
    if (numValue >= thresholds.high) return { bg: '#e8f5e9', text: '#2e7d32' }; // Vert clair/foncé
    if (numValue >= thresholds.medium) return { bg: '#fff3e0', text: '#ef6c00' }; // Orange clair/foncé
    return { bg: '#ffebee', text: '#c62828' }; // Rouge clair/foncé
  };

  // Fonction pour mettre à jour les seuils de manière optimisée
  const updateThreshold = (key, field, value) => {
    setThresholds(prev => {
      const newThresholds = {
        ...prev,
        [key]: {
          ...prev[key],
          [field]: value
        }
      };
      localStorage.setItem("analyseThresholds", JSON.stringify(newThresholds));
      return newThresholds;
    });
  };

  const handleMarkAsRead = async () => {
    try {
      console.log('🔍 Tentative de marquer comme lu le fichier:', sourceFile);
      const response = await api.post(`/api/analysed-files/${encodeURIComponent(sourceFile)}/mark-read`);
      console.log('✅ Réponse:', response.status, response.data);
      await refreshNotifications();
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Erreur lors du marquage comme lu:', error);
      
      // Afficher plus de détails sur l'erreur pour faciliter le débogage
      if (error.response) {
        console.error('Statut:', error.response.status);
        console.error('Données:', error.response.data);
        console.error('Headers:', error.response.headers);
        console.error('URL complète:', error.config.url);
      } else if (error.request) {
        console.error('Aucune réponse reçue. Requête:', error.request);
      }
      
      // Alerte utilisateur
      alert(`Erreur lors du marquage comme lu: ${error.message}`);
    }
  };

  // Fonction pour sélectionner/désélectionner toutes les lignes
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      const allProductIds = table.getRowModel().rows.map(row => 
        `${row.original.productSearch}-${row.original.countryCode || 'global'}`
      );
      setSelectedProducts(allProductIds);
    }
    setSelectAll(!selectAll);
  };

  // Fonction pour sélectionner/désélectionner une ligne
  const handleSelectRow = (product, event) => {
    event.stopPropagation(); // Empêcher le clic de sélectionner la ligne
    const productId = product.productSearch;
    
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Gérer l'ouverture du menu d'actions
  const handleOpenActionMenu = (event) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  // Gérer la fermeture du menu d'actions
  const handleCloseActionMenu = () => {
    setActionMenuAnchorEl(null);
  };

  // Fonction pour masquer les produits sélectionnés
  const handleBatchMaskProducts = async () => {
    if (selectedProducts.length === 0) return;
    
    try {
      // Pour chaque produit sélectionné
      for (const compositeId of selectedProducts) {
        // Extraire l'EAN et le code pays de la clé composite
        const [ean, countryCode] = compositeId.split('-');
        const actualCountryCode = countryCode === 'global' ? null : countryCode;
        
        // Trouver le produit dans les données
        const product = data.find(item => 
          item.productSearch === ean && 
          (item.countryCode === actualCountryCode || 
           (!item.countryCode && actualCountryCode === null))
        );
        
        if (product && !isProductHidden(product)) {
          // Masquer le produit avec tous les champs nécessaires
          const response = await api.post("/api/masques", {
            ean: product.productSearch,
            asin: product.sourceSIN,
            title: product.title,
            purchasePrice: product.purchasePrice,
            buyBoxLanded: product.buyBoxLanded,
            supplier: sourceFile,
            column1: product.column1,
            status: batchMaskStatus,
            comment: batchMaskComment,
            countryCode: product.countryCode // Ajouter le code pays pour le masquage par lot
          });
          
          setHiddenProducts(prev => [...prev, response.data]);
        }
      }
      
      // Filtrer les données pour retirer les produits masqués de la vue
      setData(prev => {
        // Créer une fonction pour vérifier si un produit est sélectionné
        const isProductSelected = item => {
          const itemCompositeId = `${item.productSearch}-${item.countryCode || 'global'}`;
          return selectedProducts.includes(itemCompositeId);
        };
        
        return prev.filter(item => !isProductSelected(item) || isProductHidden(item));
      });
      
      // Réinitialiser la sélection
      setSelectedProducts([]);
      setSelectAll(false);
      setIsConfirmBatchMaskDialogOpen(false);
      setBatchMaskComment('');
      setBatchMaskStatus('Masqué');
    } catch (error) {
      console.error('Erreur lors du masquage par lot:', error);
    }
  };

  // Fonction pour ajouter aux favoris les produits sélectionnés
  const handleBatchAddToFavorites = () => {
    if (selectedProducts.length === 0) return;
    setIsBatchFavoriteDialogOpen(true);
    handleCloseActionMenu();
  };

  // Fonction pour sauvegarder les favoris par lot
  const handleSaveBatchFavorites = async () => {
    try {
      for (const compositeId of selectedProducts) {
        // Extraire l'EAN et le code pays de la clé composite
        const [ean, countryCode] = compositeId.split('-');
        const actualCountryCode = countryCode === 'global' ? null : countryCode;
        
        // Trouver le produit dans les données
        const product = data.find(item => 
          item.productSearch === ean && 
          (item.countryCode === actualCountryCode || 
           (!item.countryCode && actualCountryCode === null))
        );
        
        if (product && !isProductFavorite(product.productSearch, product.sourceSIN, product.countryCode)) {
          const newFavorite = {
            ean: product.productSearch,
            asin: product.sourceSIN,
            title: product.title,
            purchasePrice: product.purchasePrice,
            buyBoxLanded: product.buyBoxLanded,
            supplier: sourceFile,
            comment: favoriteComment,
            status: favoriteStatus,
            column1: product.column1,
            countryCode: product.countryCode // Ajouter le code pays
          };
          
          const response = await api.post("/api/favoris", newFavorite);
          setFavorites(prevFavorites => [...prevFavorites, { ...newFavorite, id: response.data.id }]);
        }
      }
      
      // Forcer le rafraîchissement de la colonne des favoris
      setFavoritesRefreshKey(prev => prev + 1);
      
      // Réinitialiser
      setIsBatchFavoriteDialogOpen(false);
      setFavoriteComment('');
      setFavoriteStatus('Intéressant');
      setSelectedProducts([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Erreur lors de l\'ajout aux favoris par lot:', error);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      {loading ? (
        <CircularProgress />
      ) : data.length === 0 ? (
        <Typography variant="h6">Aucune donnée disponible</Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h4">Analyse de {sourceFile}</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {/* Compteur de favoris - uniquement ceux du fichier actuel */}
              {(() => {
                // Compter les favoris présents dans le fichier actuel
                const fileEANs = new Set(data.map(item => item.productSearch));
                const fileFavoritesCount = favorites.filter(fav => 
                  fileEANs.has(fav.ean)
                ).length;
                
                return (
                  <Chip
                    icon={<Star style={{ color: "#FFB900" }} />}
                    label={`${fileFavoritesCount} favoris`}
                    color="primary"
                    variant="outlined"
                  />
                );
              })()}
              
              {/* Compteur de produits masqués - uniquement ceux du fichier actuel */}
              {(() => {
                // Compter les masques présents dans le fichier actuel
                const fileEANs = new Set(data.map(item => item.productSearch));
                const fileHiddenCount = hiddenProducts.filter(p => 
                  fileEANs.has(p.ean)
                ).length;
                
                return (
                  <Chip
                    icon={<EyeOff />}
                    label={`${fileHiddenCount} masqués`}
                    color="primary"
                    variant="outlined"
                  />
                );
              })()}
              
              {unreadCount > 0 && (
                <Chip
                  icon={<AlertTriangle size={20} />}
                  label={`${unreadCount} produit(s) non lu(s)`}
                  color="warning"
                  onClick={handleMarkAsRead}
                  style={{ cursor: "pointer" }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 2, alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {/* Section de gauche: recherche et nombre de produits sélectionnés / bouton d'actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
              <TextField
                label="Recherche globale"
                variant="outlined"
                size="small"
                value={globalFilter || ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                InputProps={{
                  startAdornment: <Search size={20} style={{ marginRight: 8, color: 'action.active' }} />,
                }}
                sx={{ minWidth: 300 }}
              />
              
              {/* Zone fixe pour afficher le nombre de produits sélectionnés et le bouton d'actions */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                minWidth: 250, 
                ml: 2,
                height: 40 
              }}>
                {selectedProducts.length > 0 ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleOpenActionMenu}
                      startIcon={<List size={20} />}
                    >
                      Actions
                    </Button>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedProducts.length} produit{selectedProducts.length > 1 ? 's' : ''} sélectionné{selectedProducts.length > 1 ? 's' : ''}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Sélectionnez des produits pour afficher les actions
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Section de droite: filtres et outils */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              {/* Switch pour afficher uniquement les favoris */}
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyFavorites}
                    onChange={(e) => setShowOnlyFavorites(e.target.checked)}
                    color="primary"
                  />
                }
                label="Favoris"
                sx={{ mr: 1 }}
              />
              
              {/* Switch pour afficher uniquement les masqués */}
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyHidden}
                    onChange={(e) => setShowOnlyHidden(e.target.checked)}
                    color="primary"
                  />
                }
                label="Masqués"
              />
              
              <Tooltip title="Configurer les seuils">
                <IconButton
                  onClick={() => setIsThresholdsOpen(true)}
                  color="primary"
                >
                  <Settings size={20} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Rafraîchir les données">
                <IconButton
                  onClick={fetchAllData}
                  color="primary"
                >
                  <RefreshCw size={20} />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Marquer comme vérifié">
                <IconButton
                  onClick={() => setIsConfirmDialogOpen(true)}
                  color="success"
                >
                  <CheckCircle size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Menu pour les actions de lot */}
          <Menu
            anchorEl={actionMenuAnchorEl}
            open={Boolean(actionMenuAnchorEl)}
            onClose={handleCloseActionMenu}
          >
            <MenuItem onClick={handleBatchAddToFavorites}>
              <ListItemIcon>
                <Star size={16} />
              </ListItemIcon>
              <ListItemText>Ajouter aux favoris</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              setIsConfirmBatchMaskDialogOpen(true);
              handleCloseActionMenu();
            }}>
              <ListItemIcon>
                <EyeOff size={16} />
              </ListItemIcon>
              <ListItemText>Masquer</ListItemText>
            </MenuItem>
          </Menu>
          
          {/* Boîte de dialogue de confirmation */}
          <Dialog
            open={isConfirmDialogOpen}
            onClose={() => setIsConfirmDialogOpen(false)}
          >
            <DialogTitle>Confirmation</DialogTitle>
            <DialogContent>
              <Typography>
                Êtes-vous sûr de vouloir marquer ce fichier comme vérifié ?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsConfirmDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  setIsConfirmDialogOpen(false);
                  handleMarkAsRead();
                }}
                color="success"
                variant="contained"
              >
                Confirmer
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dialogue pour l'ajout aux favoris par lot */}
          <Dialog
            open={isBatchFavoriteDialogOpen}
            onClose={() => setIsBatchFavoriteDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Ajouter {selectedProducts.length} produit(s) aux favoris</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Vous êtes sur le point d'ajouter {selectedProducts.length} produit(s) à vos favoris.
                </Typography>
                
                <FormControl fullWidth sx={{ mt: 3 }}>
                  <InputLabel id="batch-status-label">Statut</InputLabel>
                  <Select
                    labelId="batch-status-label"
                    value={favoriteStatus}
                    onChange={(e) => setFavoriteStatus(e.target.value)}
                    label="Statut"
                  >
                    <MenuItem value="Intéressant">Intéressant</MenuItem>
                    <MenuItem value="Presque intéressant">Presque intéressant</MenuItem>
                    <MenuItem value="Plus de stock">Plus de stock</MenuItem>
                    <MenuItem value="À surveiller">À surveiller</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Commentaire (appliqué à tous les produits)"
                  multiline
                  rows={3}
                  value={favoriteComment}
                  onChange={(e) => setFavoriteComment(e.target.value)}
                  sx={{ mt: 3 }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsBatchFavoriteDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveBatchFavorites}
                color="primary"
                variant="contained"
              >
                Ajouter
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dialogue de confirmation pour masquer par lot */}
          <Dialog
            open={isConfirmBatchMaskDialogOpen}
            onClose={() => setIsConfirmBatchMaskDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Masquer les produits sélectionnés</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Vous êtes sur le point de masquer {selectedProducts.length} produit(s).
                </Typography>
                
                <FormControl fullWidth sx={{ mt: 3 }}>
                  <InputLabel id="batch-mask-status-label">Statut</InputLabel>
                  <Select
                    labelId="batch-mask-status-label"
                    value={batchMaskStatus}
                    onChange={(e) => setBatchMaskStatus(e.target.value)}
                    label="Statut"
                  >
                    {MASK_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Commentaire (appliqué à tous les produits)"
                  multiline
                  rows={3}
                  value={batchMaskComment}
                  onChange={(e) => setBatchMaskComment(e.target.value)}
                  sx={{ mt: 3 }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsConfirmBatchMaskDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleBatchMaskProducts}
                color="primary"
                variant="contained"
              >
                Masquer
              </Button>
            </DialogActions>
          </Dialog>

          {/* Boîte de dialogue pour ajouter aux favoris (individuel) */}
          <Dialog
            open={isAddFavoriteDialogOpen}
            onClose={() => setIsAddFavoriteDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Ajouter aux favoris</DialogTitle>
            <DialogContent>
              {selectedProduct && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedProduct.title}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        EAN: {selectedProduct.productSearch}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        ASIN: {selectedProduct.sourceSIN}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Prix d'achat: {selectedProduct.purchasePrice} €
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Buy Box: {selectedProduct.buyBoxLanded} €
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel id="status-label">Statut</InputLabel>
                    <Select
                      labelId="status-label"
                      value={favoriteStatus}
                      onChange={(e) => setFavoriteStatus(e.target.value)}
                      label="Statut"
                    >
                      <MenuItem value="Intéressant">Intéressant</MenuItem>
                      <MenuItem value="Presque intéressant">Presque intéressant</MenuItem>
                      <MenuItem value="Plus de stock">Plus de stock</MenuItem>
                      <MenuItem value="À surveiller">À surveiller</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Commentaire"
                    multiline
                    rows={3}
                    value={favoriteComment}
                    onChange={(e) => setFavoriteComment(e.target.value)}
                    sx={{ mt: 3 }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsAddFavoriteDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveFavorite}
                color="primary"
                variant="contained"
              >
                Ajouter
              </Button>
            </DialogActions>
          </Dialog>

          {/* Boîte de dialogue pour masquer un produit (individuel) */}
          <Dialog
            open={isAddMaskDialogOpen}
            onClose={() => setIsAddMaskDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Masquer le produit</DialogTitle>
            <DialogContent>
              {selectedProduct && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedProduct.title}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        EAN: {selectedProduct.productSearch}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        ASIN: {selectedProduct.sourceSIN}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Prix d'achat: {selectedProduct.purchasePrice} €
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Buy Box: {selectedProduct.buyBoxLanded} €
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <FormControl fullWidth sx={{ mt: 3 }}>
                    <InputLabel id="mask-status-label">Statut</InputLabel>
                    <Select
                      labelId="mask-status-label"
                      value={maskStatus}
                      onChange={(e) => setMaskStatus(e.target.value)}
                      label="Statut"
                    >
                      {MASK_STATUSES.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Commentaire"
                    multiline
                    rows={3}
                    value={maskComment}
                    onChange={(e) => setMaskComment(e.target.value)}
                    sx={{ mt: 3 }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsAddMaskDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSaveMask}
                color="primary"
                variant="contained"
              >
                Masquer
              </Button>
            </DialogActions>
          </Dialog>

          <Drawer
            anchor="right"
            open={isThresholdsOpen}
            onClose={() => setIsThresholdsOpen(false)}
            PaperProps={{
              sx: { width: 400 }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Configuration des seuils</Typography>
                <IconButton onClick={() => setIsThresholdsOpen(false)}>
                  <EyeOff size={20} />
                </IconButton>
              </Box>
              
              {Object.entries(thresholds).map(([key, threshold]) => (
                <Box key={key} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                      {key === 'estimatedNumberofSales' ? 'Nombre de ventes' : key}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={threshold.enabled}
                          onChange={(e) => updateThreshold(key, 'enabled', e.target.checked)}
                        />
                      }
                      label="Actif"
                    />
                  </Box>
                  
                  {threshold.enabled && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography gutterBottom>Seuil haut</Typography>
                        <Slider
                          value={threshold.high}
                          onChange={(_, value) => updateThreshold(key, 'high', value)}
                          min={0}
                          max={key === 'roi' ? 200 : key === 'margin' ? 100 : 1000}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                        />
                        <TextField
                          size="small"
                          value={threshold.high}
                          onChange={(e) => updateThreshold(key, 'high', Number(e.target.value))}
                          type="number"
                          sx={{ mt: 1, width: '100%' }}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography gutterBottom>Seuil moyen</Typography>
                        <Slider
                          value={threshold.medium}
                          onChange={(_, value) => updateThreshold(key, 'medium', value)}
                          min={0}
                          max={threshold.high}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                        />
                        <TextField
                          size="small"
                          value={threshold.medium}
                          onChange={(e) => updateThreshold(key, 'medium', Number(e.target.value))}
                          type="number"
                          sx={{ mt: 1, width: '100%' }}
                        />
                      </Box>
                    </>
                  )}
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Box>
          </Drawer>
          <TableContainer component={Paper}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #ccc",
                          borderRight: "1px solid #eee",
                          textAlign: "left",
                          cursor: header.column.getCanSort() ? "pointer" : "default",
                          position: "relative",
                          width: header.getSize(),
                          minWidth: 50,
                          userSelect: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            cursor: 'move',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                          draggable
                          onDragStart={(e) => {
                            setDraggedColumnId(header.column.id);
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            setDraggedColumnId(null);
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (draggedColumnId && draggedColumnId !== header.column.id) {
                              const newOrder = table.getAllLeafColumns()
                                .map(col => col.id)
                                .filter(id => id !== draggedColumnId);
                              const targetIndex = newOrder.indexOf(header.column.id);
                              newOrder.splice(targetIndex, 0, draggedColumnId);
                              table.setColumnOrder(newOrder);
                            }
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <GripVertical 
                            sx={{ 
                              mr: 0.5, 
                              opacity: 0.2,
                              fontSize: '1rem',
                              '&:hover': { opacity: 0.5 }
                            }}
                          />
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <ArrowUp size={16} style={{ marginLeft: 4 }} />,
                            desc: <ArrowDown size={16} style={{ marginLeft: 4 }} />,
                          }[header.column.getIsSorted()] ?? null}
                        </Box>
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '4px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            backgroundColor: '#e0e0e0',
                            opacity: 0.5,
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.backgroundColor = '#2196f3';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.5';
                            e.currentTarget.style.backgroundColor = '#e0e0e0';
                          }}
                        />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                <tr>
                  {table.getHeaderGroups()[0].headers.map((header) => {
                    const columnId = header.column.id;
                    const filter = filters[columnId];
                    
                    if (filter) {
                      // Filtres spéciaux pour productGroup et countryCode
                      if (columnId === 'productGroup' || columnId === 'countryCode') {
                        // Récupérer les valeurs uniques pour la colonne
                        const uniqueValues = [...new Set(data.map(item => item[columnId]))].filter(Boolean).sort();
                        
                        return (
                          <td
                            key={`filter-${columnId}`}
                            style={{
                              padding: "2px 4px",
                              borderBottom: "1px solid #eee",
                              borderRight: "1px solid #eee",
                              backgroundColor: "#f5f5f5",
                              fontSize: "0.75rem"
                            }}
                          >
                            <FormControl size="small" fullWidth>
                              <Select
                                value={filter.value || ''}
                                onChange={(e) => {
                                  const newFilter = { ...filter, value: e.target.value };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  setTimeout(() => {
                                    header.column.setFilterValue(newFilter);
                                  }, 0);
                                }}
                                size="small"
                                displayEmpty
                                sx={{ height: '28px' }}
                              >
                                <MenuItem value="">Tous</MenuItem>
                                {uniqueValues.map((value) => (
                                  <MenuItem key={value} value={value}>
                                    {columnId === 'countryCode' && countryFlags[value] ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <img 
                                          src={countryFlags[value]} 
                                          alt={`Drapeau ${value}`}
                                          style={{ 
                                            width: '16px',
                                            height: '12px',
                                            objectFit: 'cover'
                                          }}
                                        />
                                        {value}
                                      </Box>
                                    ) : value}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </td>
                        );
                      }
                      
                      // Filtres numériques standard
                      return (
                        <td
                          key={`filter-${columnId}`}
                          style={{
                            padding: "2px 4px",
                            borderBottom: "1px solid #eee",
                            borderRight: "1px solid #eee",
                            backgroundColor: "#f5f5f5",
                            fontSize: "0.75rem"
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 60 }}>
                              <Select
                                value={filter.operator}
                                onChange={(e) => {
                                  const newFilter = { ...filter, operator: e.target.value };
                                  setFilters(prev => ({
                                    ...prev,
                                    [columnId]: newFilter
                                  }));
                                  setTimeout(() => {
                                    header.column.setFilterValue(newFilter);
                                  }, 0);
                                }}
                                size="small"
                                sx={{ height: '28px' }}
                              >
                                <MenuItem value=">=">≥</MenuItem>
                                <MenuItem value=">">&gt;</MenuItem>
                                <MenuItem value="<=">≤</MenuItem>
                                <MenuItem value="<">&lt;</MenuItem>
                                <MenuItem value="=">=</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              value={filter.value}
                              onChange={(e) => {
                                const newFilter = { ...filter, value: e.target.value };
                                setFilters(prev => ({
                                  ...prev,
                                  [columnId]: newFilter
                                }));
                                setTimeout(() => {
                                  header.column.setFilterValue(newFilter);
                                }, 0);
                              }}
                              type="number"
                              sx={{ 
                                width: 60,
                                '& .MuiInputBase-root': {
                                  height: '28px'
                                }
                              }}
                            />
                          </Box>
                        </td>
                      );
                    }
                    return (
                      <td
                        key={`filter-${columnId}`}
                        style={{
                          padding: "2px 4px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid #eee",
                          backgroundColor: "#f5f5f5"
                        }}
                      />
                    );
                  })}
                </tr>
                {table.getRowModel().rows.map((row) => (
                  <tr 
                    key={row.id}
                    onClick={(e) => {
                      // Ne pas sélectionner la ligne si on clique sur un élément interactif
                      if (e.target.closest('button, a, [role="button"], .MuiIconButton-root, .MuiCheckbox-root')) {
                        return;
                      }
                      setSelectedRowId(row.id === selectedRowId ? null : row.id);
                    }}
                    onMouseEnter={() => setHoveredRowId(row.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                    style={{
                      backgroundColor: row.id === selectedRowId 
                        ? 'rgba(33, 150, 243, 0.15)' 
                        : row.id === hoveredRowId 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid #eee",
                          width: cell.column.getSize(),
                          minWidth: 50,
                          maxWidth: cell.column.getSize(),
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          position: "relative"
                        }}
                      >
                        {cell.column.id === "title" ? (
                          <Tooltip title={cell.getValue()}>
                            <span style={{
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}>
                              {cell.getValue()}
                            </span>
                          </Tooltip>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableContainer>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: Math.max(prev.pageIndex - 1, 0),
                }))
              }
              disabled={!table.getCanPreviousPage()}
            >
              Précédent
            </Button>
            <span>
              Page {pagination.pageIndex + 1} sur {pageCount}
            </span>
            <Button
              variant="contained"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  pageIndex: prev.pageIndex + 1,
                }))
              }
              disabled={!table.getCanNextPage()}
            >
              Suivant
            </Button>
          </div>
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </div>
  );
};

export default AnalysePage; 